import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { actions as reportsActions } from '../../actions/reportsActions';
import Routes from '../../constants/routes';
import * as types from '../../constants/actionTypes';
import Notification from '../Common/Notification';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import PageContainer from '../Common/PageContainer';
import Card from '../Common/Card';
import InputText from '../Common/InputText';
import MultiSelectBox from '../Common/MultiSelectBox/MultiSelectBox';
import ReportFiltersManager from './ReportFiltersManager';
import ReportDataHelper from './ReportDataHelper';
import './Reports.scss';
import { isValidEmailAddress } from '../../helpers/validation';
import { REPORT_IDS } from '../../constants/constants';

export class ScheduleReportPage extends React.Component {

    constructor(props, context) {
        super(props, context);

        this.reportDataHelper = new ReportDataHelper(props.reportsActions);

        this.state = {
            isLoading: false,
            isFormValid: true,
            filters: {
                sharedSchedules: [],
                exportFileTypes: []
            },
            allowTaskListExportToExcel: false,
            reportFilters: {},
            report: {},
            scheduledReport: {},
            ssrsFilterValues: {},
            savedFilterValues: {},
            description: '',
            owner: '',
            to: '',
            cc: '',
            message: '',
            frequencyId: '',
            exportFileTypeId: ''
        };
    }

    async componentDidMount() {
        document.title = 'Reports';
        if (this.isAddMode()) {
            // In Add Mode, set the read-only Owner field to the current user's name
            this.setState({ owner: this.props.currentUserName, isLoading: true });
            await this.loadScheduleFilters(); //this must come first to prevent a race condition
            await this.loadReportAndFilters(this.props.reportId);
            this.setState({ isLoading: false });
        } else if (this.isEditMode()) {
            this.setState({ isLoading: true });
            await this.loadScheduleFilters();
            await this.loadScheduledReport();
            this.setState({ isLoading: false });
        }
    }

    isAddMode = () => {
        // reportId comes in via route params in add mode
        if (this.props.reportId) {
            return true;
        }
        return false;
    }

    isEditMode = () => {
        // scheduledReportId comes in via route params in edit mode
        if (this.props.scheduledReportId) {
            return true;
        }
        return false;
    }

    loadScheduleFilters = async () => {
        try {
            const response = await this.props.reportsActions.loadScheduleFilters();
            if (response.type === types.LOAD_SCHEDULE_FILTERS_SUCCESS) {
                this.setState({
                    filters: response.payload.filters
                });
            } else if (response.type === types.LOAD_SCHEDULE_FILTERS_FAILURE) {
                Notification("error", "Error", response.payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error);
        }
    }

    loadReportAndFilters = async (reportId) => {
        try {
            const response = await this.props.reportsActions.loadReportFilters(reportId);
            if (response.type === types.LOAD_REPORT_FILTERS_SUCCESS) {

                let filters = this.state.filters;
                //if allowTaskListExportToExcel is false we want to filter it out of the options
                if (filters && (reportId === REPORT_IDS.TaskList || reportId === REPORT_IDS.PastDueTaskList) && !response.payload.allowTaskListExportToExcel ) {
                    let exportFileTypes = filters.exportFileTypes.filter(i => i.id !== "Excel");
                    filters = {
                        ...filters,
                        exportFileTypes
                    }
                }

                this.setState({
                    reportFilters:  (response.payload.filters) ? response.payload.filters : {},
                    report: response.payload.report,
                    filters
                });
            } else if (response.type === types.LOAD_REPORT_FILTERS_FAILURE) {
                Notification("error", "Error", response.payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error);
        }
    }

    loadScheduledReport = async () => {
        try {
            const response = await this.props.reportsActions.loadScheduledReport(this.props.scheduledReportId);
            if (response.type === types.LOAD_SCHEDULED_REPORT_SUCCESS) {
                const scheduledReport = response.payload.scheduledReport;
                const reportRequest = scheduledReport.reportRequest
                const reportId = reportRequest.reportId;
                
                await this.loadReportAndFilters(reportId);

                // Have to temporarily store the report request in state, because report filters
                // have not yet mounted and registered their mapToForm functions
                this.setState({
                    savedFilterValues: response.payload.scheduledReport.reportRequest,
                    reportRequest: reportRequest,
                    description: scheduledReport.name,
                    owner: scheduledReport.owner,
                    to: scheduledReport.to,
                    cc: scheduledReport.cc,
                    message: scheduledReport.message,
                    frequencyId: scheduledReport.frequencyId,
                    exportFileTypeId: scheduledReport.exportFileTypeId
                });
            } else if (response.type === types.LOAD_SCHEDULED_REPORT_FAILURE) {
                Notification("error", "Error", response.payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error);
        }
    }

    loadLocationReponsibleUsers = async (locationKeys) => {
        try {
            const response = await this.props.reportsActions.loadLocationResponsibleUsers(locationKeys);
            if (response.type === types.LOAD_REPORT_LOCATION_RESPONSIBLE_USERS_SUCCESS) {
                this.setState({
                    isLoading: false,
                    reportFilters: {
                        ...this.state.reportFilters,
                        responsibleUsers: response.payload.filters.responsibleUsers
                    }
                });
            } else if (response.type === types.LOAD_REPORT_LOCATION_RESPONSIBLE_USERS_FAILURE) {
                Notification("error", "Error", response.payload.message);
                this.setState({ isLoading: false });
            }
        } catch (error) {
            Notification("error", "Error", error);
            this.setState({ isLoading: false });
        }
    }

    loadProjectDependentFilters = async (projectId) => {
        try {
            const response = await this.props.reportsActions.loadProjectDependentFilters(this.state.report.id, projectId);
            if (response.type === types.LOAD_REPORT_PROJECT_DEPENDENT_FILTERS_SUCCESS) {
                this.setState({
                    isLoading: false,
                    reportFilters: {
                        ...this.state.reportFilters,
                        projectBaselines: response.payload.filters.projectBaselines,
                        tasks: response.payload.filters.tasks,
                        projectZeroDates: response.payload.filters.projectZeroDates,
                    }
                });
            } else if (response.type === types.LOAD_REPORT_PROJECT_DEPENDENT_FILTERS_FAILURE) {
                Notification("error", "Error", response.payload.message);
                this.setState({ isLoading: false });
            }
        } catch (error) {
            Notification("error", "Error", error);
            this.setState({ isLoading: false });
        }
    }

    loadLocationsForCoachFilters = async (coachId) => {
        try {
            const response = await this.props.reportsActions.loadLocationsForCoach(coachId);
            if (response.type === types.LOAD_COACH_LOCATIONS_FILTERS_SUCCESS) {
                this.setState({
                    reportFilters: {
                        ...this.state.reportFilters,
                        coachLocations: response.payload.filters.locations
                    }
                });
            } else if (response.type === types.LOAD_COACH_LOCATIONS_FILTERS_FAILURE) {
                Notification("error", "Error", response.payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error);
        } finally {
            this.setState({ isLoading: false });
        }
    }

    onSaveClick = async () => {
        if (!this.isFormValid()) {
            this.setState({ isFormValid: false });
            return;
        }

        if (this.isAddMode()) {
            try {
                this.setState({ isFormValid: true, isLoading: true });
                const reportRequest = this.reportDataHelper.mapToApiRequest(this.state.report.id);
                const request = {
                    description: this.state.description,
                    scheduleId: this.state.frequencyId,
                    to: this.state.to,
                    cc: this.state.cc,
                    message: this.state.message,
                    exportFileType: this.state.exportFileTypeId,
                    reportRequest: reportRequest
                };
                const response = await this.props.reportsActions.createScheduledReport(request);
                if (response.type === types.CREATE_SCHEDULED_REPORT_SUCCESS) {
                    Notification("success", "Success", "Report scheduled successfully");
                    this.props.history.push(Routes.Reports);
                } else if (response.type === types.CREATE_SCHEDULED_REPORT_FAILURE) {
                    Notification("error", "Error", response.payload.message);
                    this.setState({ isLoading: false });
                }
            } catch (error) {
                Notification("error", "Error", error);
                this.setState({ isLoading: false });
            }
        } else if (this.isEditMode()) {
            try {
                this.setState({ isFormValid: true, isLoading: true });
                const reportRequest = this.reportDataHelper.mapToApiRequest(this.state.report.id);
                const request = {
                    id: this.props.scheduledReportId,
                    description: this.state.description,
                    scheduleId: this.state.frequencyId,
                    to: this.state.to,
                    cc: this.state.cc,
                    message: this.state.message,
                    exportFileType: this.state.exportFileTypeId,
                    reportRequest: reportRequest
                };
                const response = await this.props.reportsActions.updateScheduledReport(request);
                if (response.type === types.UPDATE_SCHEDULED_REPORT_SUCCESS) {
                    Notification("success", "Success", "Scheduled Report updated successfully");
                    this.props.history.push(Routes.Reports);
                } else if (response.type === types.UPDATE_SCHEDULED_REPORT_FAILURE) {
                    Notification("error", "Error", response.payload.message);
                    this.setState({ isLoading: false });
                }
            } catch (error) {
                Notification("error", "Error", error);
                this.setState({ isLoading: false });
            }
        }
    }

    isFormValid = () => {
        return this.isScheduleFormValid() && this.isReportFormValid();
    }

    isScheduleFormValid = () => {
        const validName = this.state.description && this.state.description.trim().length > 0;
        const validTo = this.state.to && this.state.to.trim().length > 0;

        let allEmailsValid = true;
        if (validTo){
            
            //validate each email address
            let emailAddresses = this.state.to.split(";");

            emailAddresses.forEach((item) => {
                if (item.trim() && !isValidEmailAddress(item.trim())) {
                    allEmailsValid = false;
                    Notification("error", "Error", `${item} is not a valid email address`);
                }
            })
        }
        
        if (this.state.cc && this.state.cc.trim().length > 0){
            
            //validate each email address
            let emailAddresses = this.state.cc.split(";");

            emailAddresses.forEach((item) => {
                if (item.trim() && !isValidEmailAddress(item.trim())) {
                    allEmailsValid = false;
                    Notification("error", "Error", `${item} is not a valid email address`);
                }
            })
        }

        const validMessage = this.state.message && this.state.message.trim().length > 0;
        const validFrequency = this.state.frequencyId && this.state.frequencyId.trim().length > 0;
        const validExportFileType = this.state.exportFileTypeId && this.state.exportFileTypeId.trim().length > 0;

        return validName && validTo && validMessage && validFrequency && validExportFileType && allEmailsValid;
    }

    isReportFormValid = () => {
        return this.reportDataHelper.validateForm(this.state.report.id);
    }

    onCancelClick = () => {
        this.props.history.goBack();
    }

    onFieldChange = (name, value) => {
        this.setState({ [name]: value });
    }

    onTextChange = (e) => {
        this.onFieldChange(e.target.name, e.target.value);
    }

    onDropdownChange = (value, name) => {
        this.onFieldChange(name, value);
    }

    registerReportFilterFunctions = (reportId, clearForm, validateForm, mapToForm, mapToApiRequest) => {
        this.reportDataHelper.registerReportFilterFunctions(reportId, clearForm, validateForm, mapToForm, mapToApiRequest);

        /**
         * In Edit mode, once a report filters component has loaded and registered its functions,
         * we need to call its mapToForm function with the stored reportRequest from the API.
         * That loads all the proper saved selected values on the report filters form.
         */
        if (this.isEditMode()) {
            const reportRequest = this.state.reportRequest;
            this.reportDataHelper.mapToForm(reportId, reportRequest);
        }
    }

    renderFilters = () => {
        const report = this.state.report;
        const filters = this.state.reportFilters;
        const urlParams = {}; // No URL params accepted when scheduling, so pass in a blank object
        const savedFilterValues = this.props.savedFilterValues;
        
        const isFormValid = this.state.isFormValid;
        const registerReportFilterFunctions = this.registerReportFilterFunctions;
        const onLocationChange = this.loadLocationReponsibleUsers;
        const onProjectChange = this.loadProjectDependentFilters;
        const isSchedulingMode = true;

        return (
            <ReportFiltersManager
                report={report}
                filters={filters}
                urlParams={urlParams}
                savedFilterValues={savedFilterValues}
                isFormValid={isFormValid}
                registerReportFilterFunctions={registerReportFilterFunctions}
                onLocationChange={onLocationChange}
                onProjectChange={onProjectChange}
                isSchedulingMode={isSchedulingMode}
                onCoachChange={this.loadLocationsForCoachFilters}
            />
        );
    }

    render() {
        return (
            <PageContainer className="scheduled-report-view-page">
                <LoadingIndicator isLoading={this.state.isLoading}>
                    <Card title={"Schedule " + this.state.report.name}>
                        <form noValidate className={this.state.isFormValid ? "" : "was-validated"}>
                            <div className="headered-section ruled-section">
                                <h2>Name this scheduled report</h2>
                                <div className="row">
                                    <div className="col-sm-12 col-md-4">
                                        <InputText name="description" label="Name" type="text" placeholder="Name" value={this.state.description} onChange={this.onTextChange}
                                            isRequired={true} errorMessage="Name is required" />
                                        <span className="help-text">This helps find this schedule later if you need to make changes</span>
                                    </div>
                                    <div className="col-sm-12 col-md-4">
                                        <InputText isDisabled={true} name="owner" label="Owner" type="text" placeholder="Owner" value={this.state.owner} />
                                    </div>
                                </div>
                            </div>
                            <div className="headered-section ruled-section">
                                <h2>Who should receive this report</h2>
                                <div className="row">
                                    <div className="col-sm-12 col-md-4">
                                        <InputText name="to" label="TO" type="text" placeholder="TO" value={this.state.to} onChange={this.onTextChange} maxLength={2000}
                                            isRequired={true} errorMessage="TO is required" />
                                        <span className="help-text">Separate multiple addresses with semicolons</span>
                                    </div>
                                    <div className="col-sm-12 col-md-4">
                                        <InputText name="cc" label="CC" type="text" placeholder="CC" value={this.state.cc} onChange={this.onTextChange} maxLength={2000} />
                                        <span className="help-text">Separate multiple addresses with semicolons</span>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-sm-12 col-md-8">
                                        <InputText name="message" label="Message" type="text" placeholder="Message" value={this.state.message} onChange={this.onTextChange}
                                            isRequired={true} errorMessage="Message is required" />
                                    </div>
                                </div>
                            </div>
                            <div className="ruled-section">
                                <div className="row">
                                    <div className="col-sm-12 col-md-4 headered-section">
                                        <h2>When should it be sent</h2>
                                        <MultiSelectBox name="frequencyId"
                                            label="Frequency" idField="id" valueField="name"
                                            options={this.state.filters.sharedSchedules}
                                            selectedValues={this.state.frequencyId.toString()}
                                            onChange={this.onDropdownChange}
                                            isRequired={true}
                                            errorMessage="Frequency is required"
                                            isMulti={false} />
                                        <span className="help-text">No Adjustment for Daylight Savings Time</span>
                                    </div>
                                    <div className="col-sm-12 col-md-4 headered-section">
                                        <h2>What file type</h2>
                                        <MultiSelectBox name="exportFileTypeId"
                                            label="Export File Type" idField="id" valueField="name"
                                            options={this.state.filters.exportFileTypes}
                                            selectedValues={this.state.exportFileTypeId.toString()}
                                            onChange={this.onDropdownChange}
                                            isRequired={true}
                                            errorMessage="Export File Type is required"
                                            isMulti={false} />
                                    </div>
                                </div>
                            </div>
                        </form>

                        <div className="headered-section ruled-section report-filters">
                            <h2>What filters should be used</h2>
                            {this.state.isLoading === false && this.state.savedFilterValues && this.renderFilters()}
                        </div>

                        <div className="form-buttons">
                            <button type="button" className="btn btn-primary" onClick={this.onSaveClick}>Save</button>
                            <button type="button" className="btn btn-secondary" onClick={this.onCancelClick}>Cancel</button>
                        </div>
                    </Card>
                </LoadingIndicator>
            </PageContainer>
        );
    }
}

ScheduleReportPage.propTypes = {
    reportId: PropTypes.number,
    scheduledReportId: PropTypes.number,
    savedFilterValues: PropTypes.object,
    currentUserName: PropTypes.string,
    history: PropTypes.object.isRequired,
    reportsActions: PropTypes.object.isRequired
};

function mapStateToProps(state, ownProps) {
    // Add Mode URL takes in a reportId from the route params
    let reportId = null;
    if (ownProps.match.params.reportId) {
        reportId = Number.parseInt(ownProps.match.params.reportId);
    }

    // Edit Mode URL takes in a scheduledReportId from the route params
    let scheduledReportId = null;
    if (ownProps.match.params.scheduledReportId) {
        scheduledReportId = Number.parseInt(ownProps.match.params.scheduledReportId);
    }

    let savedFilterValues = {};
    if (state.reports && state.reports.filterModels && reportId && state.reports.filterModels[reportId.toString()]) {
        savedFilterValues = state.reports.filterModels[reportId];
    }

    let currentUserName = '';
    if (state.user && state.user.userDisplayName) {
        currentUserName = state.user.userDisplayName;
    }

    return {
        reportId: reportId,
        scheduledReportId: scheduledReportId,
        savedFilterValues: savedFilterValues,
        currentUserName: currentUserName,
        history: ownProps.history
    }
}

function mapDispatchToProps(dispatch) {
    return {
        reportsActions: bindActionCreators(reportsActions, dispatch),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(ScheduleReportPage);
